// Constants for expected CSV column names (normalized form)
export const CSV_COLUMNS = {
  BOOKING_ID: 'booking_id',
  START_TIME: 'start_time',
  END_TIME: 'end_time',
} as const

// Normalize a header string: lowercase, replace spaces/hyphens with underscores, strip extra whitespace
const normalizeHeader = (header: string): string =>
  header
    .trim()
    .toLowerCase()
    .replace(/[\s\-]+/g, '_')
    .replace(/^"|"$/g, '')

export interface CsvTimestampEntry {
  booking_id: string
  start_time: string
  end_time: string
}

export interface CsvParseResult {
  entries: CsvTimestampEntry[]
  error?: string
}

export const parseCsvForTimestamps = (csvText: string): CsvParseResult => {
  const lines = csvText.split(/\r?\n/).filter(line => line.trim())

  if (lines.length < 2) {
    return { entries: [], error: 'CSV file is empty or has no data rows' }
  }

  const rawHeaders = lines[0].split(',')
  const headers = rawHeaders.map(normalizeHeader)

  const bookingIdIdx = headers.indexOf(CSV_COLUMNS.BOOKING_ID)
  const startTimeIdx = headers.indexOf(CSV_COLUMNS.START_TIME)
  const endTimeIdx = headers.indexOf(CSV_COLUMNS.END_TIME)

  if (bookingIdIdx === -1 || startTimeIdx === -1 || endTimeIdx === -1) {
    const missing = [
      bookingIdIdx === -1 && CSV_COLUMNS.BOOKING_ID,
      startTimeIdx === -1 && CSV_COLUMNS.START_TIME,
      endTimeIdx === -1 && CSV_COLUMNS.END_TIME,
    ]
      .filter(Boolean)
      .join(', ')
    return {
      entries: [],
      error: `CSV is missing required columns: ${missing}. Column names are flexible — e.g. "Booking Id", "booking_id", "booking-id" all work.`,
    }
  }

  const entries: CsvTimestampEntry[] = []
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(',').map(c => c.trim().replace(/^"|"$/g, ''))
    const bookingId = cols[bookingIdIdx]
    if (bookingId) {
      entries.push({
        booking_id: bookingId,
        start_time: cols[startTimeIdx] || '',
        end_time: cols[endTimeIdx] || '',
      })
    }
  }

  return { entries }
}
